import { useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import Modal from "@/components/base/Modal";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useFormules } from "@/store/formules/FormulesProvider";
import { useBilling } from "@/store/billing/BillingProvider";
import { COMPTE_TYPE_CONFIG } from "@/pages/super-admin/data/types";
import {
  ABONNEMENT_STATUT_LABELS,
  ABONNEMENT_STATUT_OPTIONS,
  ABONNEMENT_STATUT_TONES,
  PAIEMENT_METHODE_LABELS,
  PAIEMENT_STATUT_LABELS,
  paiementReference,
  type AbonnementStatut,
  type PlatformAbonnement,
} from "@/store/billing/types";
import { formatDate, formatFcfa } from "@/utils/format";
import type { ExportColumn } from "@/utils/export";
import ConfirmationModal from "@/pages/super-admin/components/ConfirmationModal";
import AbonnementForm from "@/pages/super-admin/components/AbonnementForm";

type SortKey = "recent" | "compte" | "montant";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "compte", label: "Compte (A → Z)" },
  { value: "montant", label: "Montant (décroissant)" },
];

const PERIODICITE_FILTERS = [
  { value: "", label: "Toutes les périodicités" },
  { value: "mensuel", label: "Mensuelle" },
  { value: "annuel", label: "Annuelle" },
];

const COMPTE_TYPE_FILTERS = [
  { value: "", label: "Tous les types de compte" },
  { value: "proprietaire", label: "Propriétaires" },
  { value: "agence", label: "Agences" },
  { value: "utilisateur", label: "Utilisateurs" },
];

export default function SuperAdminAbonnementsPage() {
  const { formules } = useFormules();
  const {
    abonnements,
    paiements,
    chargement,
    erreur,
    recharger,
    removeAbonnement,
    changeAbonnementStatut,
    countPaiementsByAbonnement,
    paiementsByAbonnement,
  } = useBilling();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformAbonnement | null>(null);
  const [detail, setDetail] = useState<PlatformAbonnement | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlatformAbonnement | null>(null);
  const [pendingStatut, setPendingStatut] = useState<{ abonnement: PlatformAbonnement; statut: AbonnementStatut } | null>(
    null
  );

  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [formuleFilter, setFormuleFilter] = useState("");
  const [periodiciteFilter, setPeriodiciteFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const formuleNom = (id: string) => formules.find((formule) => formule.id === id)?.nom ?? id;

  const stats = useMemo(
    () => ({
      total: abonnements.length,
      actifs: abonnements.filter((item) => item.statut === "actif").length,
      attente: abonnements.filter((item) => item.statut === "en_attente").length,
      revenus: paiements.filter((item) => item.statut === "valide").reduce((sum, item) => sum + item.montant, 0),
    }),
    [abonnements, paiements]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = abonnements.filter((item) => {
      if (statutFilter && item.statut !== statutFilter) return false;
      if (formuleFilter && item.formuleId !== formuleFilter) return false;
      if (periodiciteFilter && item.periodicite !== periodiciteFilter) return false;
      if (typeFilter && item.compteType !== typeFilter) return false;
      if (term && !`${item.compteNom} ${formuleNom(item.formuleId)}`.toLowerCase().includes(term)) return false;
      return true;
    });
    return result.sort((a, b) => {
      if (sort === "compte") return a.compteNom.localeCompare(b.compteNom, "fr");
      if (sort === "montant") return b.montant - a.montant;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [abonnements, search, statutFilter, formuleFilter, periodiciteFilter, typeFilter, sort]);

  const isFiltering = Boolean(search.trim() || statutFilter || formuleFilter || periodiciteFilter || typeFilter);

  const exportColumns: ExportColumn<PlatformAbonnement>[] = [
    { header: "Compte", value: (item) => item.compteNom },
    { header: "Type de compte", value: (item) => item.compteType },
    { header: "Formule", value: (item) => formuleNom(item.formuleId) },
    { header: "Périodicité", value: (item) => item.periodicite },
    { header: "Montant", value: (item) => item.montant },
    { header: "Début", value: (item) => item.dateDebut },
    { header: "Fin", value: (item) => item.dateFin },
    { header: "Renouvellement", value: (item) => (item.renouvellement ? "Oui" : "Non") },
    { header: "Statut", value: (item) => ABONNEMENT_STATUT_LABELS[item.statut] },
  ];

  const resetFilters = () => {
    setSearch("");
    setStatutFilter("");
    setFormuleFilter("");
    setPeriodiciteFilter("");
    setTypeFilter("");
  };

  const columns: DataTableColumn<PlatformAbonnement>[] = [
    {
      key: "compte",
      header: "Compte",
      className: "min-w-[190px]",
      render: (abonnement) => (
        <div className="flex flex-col">
          <span className="font-label font-medium text-foreground-950">{abonnement.compteNom}</span>
          <span className="text-xs text-foreground-500">
            {COMPTE_TYPE_CONFIG[abonnement.compteType as keyof typeof COMPTE_TYPE_CONFIG]?.singular ?? "Compte"}
          </span>
        </div>
      ),
    },
    {
      key: "formule",
      header: "Formule",
      className: "min-w-[130px]",
      render: (abonnement) => <span className="text-foreground-700">{formuleNom(abonnement.formuleId)}</span>,
    },
    {
      key: "periodicite",
      header: "Périodicité",
      className: "min-w-[110px]",
      render: (abonnement) => (
        <span className="text-foreground-700">
          {abonnement.periodicite === "annuel" ? "Annuelle" : "Mensuelle"}
        </span>
      ),
    },
    {
      key: "montant",
      header: "Montant",
      align: "right",
      className: "min-w-[110px]",
      render: (abonnement) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(abonnement.montant)}</span>
      ),
    },
    {
      key: "periode",
      header: "Période",
      className: "min-w-[200px]",
      render: (abonnement) => (
        <span className="text-foreground-700">
          {abonnement.dateDebut ? formatDate(abonnement.dateDebut) : "—"} →{" "}
          {abonnement.dateFin ? formatDate(abonnement.dateFin) : "en cours"}
        </span>
      ),
    },
    {
      key: "paiements",
      header: "Paiements",
      align: "center",
      className: "min-w-[90px]",
      render: (abonnement) => (
        <span className="font-label font-medium text-foreground-950">
          {countPaiementsByAbonnement(abonnement.id)}
        </span>
      ),
    },
    {
      key: "statut",
      header: "Statut",
      render: (abonnement) => (
        <Badge tone={ABONNEMENT_STATUT_TONES[abonnement.statut]}>
          {ABONNEMENT_STATUT_LABELS[abonnement.statut]}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[320px]",
      render: (abonnement) => {
        const statutAction: AbonnementStatut | null =
          abonnement.statut === "actif" ? "suspendu" : abonnement.statut === "suspendu" ? "actif" : null;
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setDetail(abonnement)}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
            >
              Voir le détail
              <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
            </button>
            {statutAction && (
              <button
                type="button"
                onClick={() => setPendingStatut({ abonnement, statut: statutAction })}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
              >
                <i
                  className={statutAction === "actif" ? "ri-checkbox-circle-line text-base" : "ri-pause-circle-line text-base"}
                  aria-hidden="true"
                />
                {statutAction === "actif" ? "Activer" : "Suspendre"}
              </button>
            )}
            <CardActions
              onEdit={() => {
                setEditing(abonnement);
                setFormOpen(true);
              }}
              onDelete={() => setPendingDelete(abonnement)}
            />
          </div>
        );
      },
    },
  ];

  const confirmDelete = () => {
    if (!pendingDelete) return;
    if (countPaiementsByAbonnement(pendingDelete.id) > 0) return;
    removeAbonnement(pendingDelete.id);
    setPendingDelete(null);
  };

  const confirmStatut = () => {
    if (!pendingStatut) return;
    changeAbonnementStatut(pendingStatut.abonnement.id, pendingStatut.statut);
    setPendingStatut(null);
  };

  const detailPaiements = detail ? paiementsByAbonnement(detail.id) : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Abonnements</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Suivi des abonnements à la plateforme : compte concerné, formule, période, montant et statut. Chaque
            abonnement est rattaché à un compte réel ; les prix proviennent de la source de vérité des formules.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu
            rows={filtered}
            columns={exportColumns}
            filename="super-admin-abonnements"
            title="Abonnements"
            subtitle="Données actuellement affichées"
          />
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Nouvel abonnement
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-vip-diamond-line" label="Abonnements" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-time-line" label="En attente" value={String(stats.attente)} tone="accent" />
        <StatCard icon="ri-money-cny-circle-line" label="Revenus validés" value={formatFcfa(stats.revenus)} tone="neutral" />
      </div>

      {abonnements.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="abo-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="abo-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Compte, formule..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="abo-f-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="abo-f-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={[{ value: "", label: "Tous les statuts" }, ...ABONNEMENT_STATUT_OPTIONS]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="abo-f-formule" className="text-xs font-label font-medium text-foreground-700">
                Formule
              </label>
              <Select
                id="abo-f-formule"
                value={formuleFilter}
                onChange={(event) => setFormuleFilter(event.target.value)}
                options={[{ value: "", label: "Toutes les formules" }, ...formules.map((f) => ({ value: f.id, label: f.nom }))]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="abo-f-periode" className="text-xs font-label font-medium text-foreground-700">
                Périodicité
              </label>
              <Select
                id="abo-f-periode"
                value={periodiciteFilter}
                onChange={(event) => setPeriodiciteFilter(event.target.value)}
                options={PERIODICITE_FILTERS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="abo-f-type" className="text-xs font-label font-medium text-foreground-700">
                Type de compte
              </label>
              <Select
                id="abo-f-type"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                options={COMPTE_TYPE_FILTERS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="abo-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="abo-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} abonnement{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {abonnements.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {chargement && abonnements.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void recharger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && abonnements.length === 0 && (
        <EmptyState
          icon="ri-vip-diamond-line"
          title="Aucun abonnement"
          description="Les abonnements de la plateforme apparaîtront ici. Créez le premier abonnement en le rattachant à un compte existant."
          action={
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Nouvel abonnement
            </button>
          }
        />
      )}

      {abonnements.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun abonnement ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(abonnement) => abonnement.id}
          minWidthClass="min-w-[1380px]"
          caption="Abonnements"
        />
      )}

      <AbonnementForm open={formOpen} onClose={() => setFormOpen(false)} abonnement={editing} />

      <Modal open={Boolean(detail)} onClose={() => setDetail(null)} size="md" title={detail?.compteNom}>
        {detail && (
          <div className="flex flex-col gap-4">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-foreground-500">Formule</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">{formuleNom(detail.formuleId)}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Périodicité</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">
                  {detail.periodicite === "annuel" ? "Annuelle" : "Mensuelle"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Montant</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">{formatFcfa(detail.montant)}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Statut</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">
                  {ABONNEMENT_STATUT_LABELS[detail.statut]}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Début</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">
                  {detail.dateDebut ? formatDate(detail.dateDebut) : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Fin</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">
                  {detail.dateFin ? formatDate(detail.dateFin) : "—"}
                </dd>
              </div>
            </dl>

            <div className="border-t border-background-200 pt-4">
              <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                Paiements associés
              </p>
              {detailPaiements.length === 0 ? (
                <p className="mt-2 text-xs text-foreground-500">
                  Aucun paiement associé. Les paiements se gèrent depuis le module Paiements.
                </p>
              ) : (
                <ul className="mt-2 flex flex-col divide-y divide-background-200">
                  {detailPaiements.map((paiement) => (
                    <li key={paiement.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">
                          {paiementReference(paiement.id)}
                        </p>
                        <p className="truncate text-xs text-foreground-500">
                          {PAIEMENT_METHODE_LABELS[paiement.methode]} · {formatDate(paiement.date)}
                        </p>
                      </div>
                      <span className="shrink-0 text-right">
                        <span className="block text-sm font-label font-medium text-foreground-950">
                          {formatFcfa(paiement.montant)}
                        </span>
                        <span className="block text-[11px] text-foreground-500">
                          {PAIEMENT_STATUT_LABELS[paiement.statut]}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={pendingDelete ? `Supprimer l'abonnement de « ${pendingDelete.compteNom} » ? Cette action est définitive.` : ""}
        blockedMessage={
          pendingDelete && countPaiementsByAbonnement(pendingDelete.id) > 0
            ? `Cet abonnement possède ${countPaiementsByAbonnement(pendingDelete.id)} paiement(s) associé(s). Supprimez d'abord ces paiements pour éviter des données orphelines.`
            : undefined
        }
      />

      <ConfirmationModal
        open={Boolean(pendingStatut)}
        onClose={() => setPendingStatut(null)}
        onConfirm={confirmStatut}
        title={pendingStatut?.statut === "actif" ? "Activer l'abonnement" : "Suspendre l'abonnement"}
        description={
          pendingStatut
            ? pendingStatut.statut === "actif"
              ? `Réactiver l'abonnement de « ${pendingStatut.abonnement.compteNom} » ?`
              : `Suspendre l'abonnement de « ${pendingStatut.abonnement.compteNom} » ? Le compte conservera ses données.`
            : ""
        }
        confirmLabel={pendingStatut?.statut === "actif" ? "Activer" : "Suspendre"}
        icon={pendingStatut?.statut === "actif" ? "ri-checkbox-circle-line" : "ri-pause-circle-line"}
        tone={pendingStatut?.statut === "actif" ? "primary" : "danger"}
      />
    </div>
  );
}